import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AiOutlineClose } from "react-icons/ai";
import { useArbolDocumentalService } from '../services/api';
import DocTreeSidebar from "../components/DocTreeSidebar/DocTreeSidebar";
import Breadcrumb from "../components/Breadcrumb";
import Buscador from "../components/Buscador";
import { OPTIONAL_COLUMNS } from "../config/optionalColumns";
import { SYSTEM_SEARCH_FIELDS } from "../config/searchFields";
import { useNotification } from "../hooks/useNotification";

const DocTree = () => {
  const { "*": splatPath } = useParams();
  const arbolService = useArbolDocumentalService();
  const navigate = useNavigate();
  const notify = useNotification();

  // Estados
  const [folders, setFolders] = useState([]);
  // selectedPath: nodos seleccionados desde root hasta el actual.
  // Forma de cada nodo: {id, name, isSeries, folders}.
  // Post ADR-022 (árbol N niveles): no hay tabla tree_level — la profundidad
  // se deriva de selectedPath.length, no de un campo `level` en el folder.
  const [selectedPath, setSelectedPath] = useState([]);
  const [paginacion, setPaginacion] = useState({ total: 0, page: 0, size: 5 });

  const hasLoadedRef = useRef(false);
  const intentosRef = useRef(0);

  /**
   * Cuántas veces se reintenta la carga del árbol antes de rendirse.
   *
   * <p>El `catch` re-armaba el guard sin ningún tope, y como `arbolService` es un objeto nuevo en
   * cada render y `notify.error` cambia estado, cada fallo disparaba un render que disparaba otro
   * intento: un bucle a velocidad de render. Con la sesión vencida eso producía **33 requests por
   * segundo** contra una API que respondía 401 a todas — los 9414 del incidente que originó #168
   * nunca fueron un poller, eran esto (#178).
   */
  const MAX_INTENTOS = 3;

  // Cargar árbol al montar (solo una vez)
  useEffect(() => {
    if (hasLoadedRef.current) return;

    hasLoadedRef.current = true;
    arbolService.getArbolDocumental()
      .then((response) => {
        console.log('Árbol cargado:', response.data);
        intentosRef.current = 0;
        setFolders(response.data.folders || []);
      })
      .catch((err) => {
        console.error("Error cargando árbol documental:", err);
        intentosRef.current += 1;
        if (intentosRef.current >= MAX_INTENTOS) {
          // Se avisa una sola vez y no se vuelve a intentar solo: recargar es del usuario.
          notify.error("No se pudo cargar el árbol documental. Recargá la página.");
          return;
        }
        notify.error("Error al cargar el árbol documental");
        hasLoadedRef.current = false;
      });
  }, [arbolService, notify]);

  // Sincronizar URL con estado (Path dinámico)
  useEffect(() => {
    if (folders.length > 0) {
      const ids = splatPath ? splatPath.split('/').filter(id => id) : [];
      let currentFolders = folders;
      let newPath = [];

      for (const id of ids) {
        const found = currentFolders.find(f => String(f.id) === id);
        if (found) {
          newPath.push(found);
          currentFolders = found.folders || [];
        } else {
          break; // O manejar error 404
        }
      }
      setSelectedPath(newPath);
      // La lista de resultados NO se limpia acá: es estado privado del `Buscador` y se va con el
      // remonte que fuerza su `key`. Lo que sí vive en esta pantalla es la paginación.
      setPaginacion({ total: 0, page: 0, size: 5 });
    }
  }, [splatPath, folders]);

  const handleFolderClick = (path) => {
    const url = path.map(f => f.id).join('/');
    navigate(`/doctree/${url}`);
  };

  const handleBreadcrumbClick = (index) => {
    if (index === -1) {
      navigate('/doctree');
    } else {
      const newPath = selectedPath.slice(0, index + 1);
      const url = newPath.map(f => f.id).join('/');
      navigate(`/doctree/${url}`);
    }
  };

  const currentSelection = selectedPath[selectedPath.length - 1];
  const isDocSerie = currentSelection?.isSeries;

  const handleFiltrarResultados = (docs, pagInfo) => {
    setPaginacion(pagInfo);
  };

  return (
    <div style={{ display: "flex", height: "100%" }}>
      <DocTreeSidebar
        folders={folders}
        selectedPath={selectedPath}
        onFolderClick={handleFolderClick}
      />
      <main style={{ flex: 1, padding: "20px", display: "flex", flexDirection: "column" }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
          <Breadcrumb
            path={selectedPath}
            onBreadcrumbClick={handleBreadcrumbClick}
          />
          <button
            style={styles.backButton}
            onClick={() => navigate('/')}
          >
            <AiOutlineClose style={styles.backIcon} />
            Cerrar
          </button>
        </div>

        <div style={{ flex: 1 }}>
          {isDocSerie && (
            <Buscador
              // Issue UBA #50: sin `key` el buscador NO se remonta al cambiar de serie —cambia
              // `tipoSeleccionado` y nada más—, así que sus `resultados` sobreviven y el usuario ve
              // los documentos de la serie anterior bajo los filtros ya vacíos. Es la misma
              // decisión que toma Control de Calidad (`ApprovalPage.jsx`), donde el remonte se ata
              // a la serie a propósito. De paso resuelve la carrera: una búsqueda de la serie
              // anterior que llegue tarde ya no tiene componente al que pisarle el estado.
              key={currentSelection.id}
              tipoSeleccionado={currentSelection.id}
              paginacion={paginacion}
              setPaginacion={setPaginacion}
              onFiltrarResultados={handleFiltrarResultados}
              optionalColumns={OPTIONAL_COLUMNS}
              columnPrefsKey="doctree"
              systemFields={SYSTEM_SEARCH_FIELDS}
            />
          )}
          {!isDocSerie && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
              Selecciona una serie documental para ver los filtros de búsqueda
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const styles = {
  backButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 16px',
    backgroundColor: '#6B7280',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  backIcon: {
    fontSize: '16px',
  }
};

export default DocTree;
